import PropTypes from 'prop-types'
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'

import classnames from 'classnames'
import { withFormsy } from 'formsy-react'
import { uniq } from 'lodash'

import { CodeEditor } from './CodeEditor'

const propTypes = {
  keywords: PropTypes.array,
  meta: PropTypes.string,
  mode: PropTypes.string,
  name: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
  setValue: PropTypes.func,
  setOptions: PropTypes.object,
  enableCreateAutocompletion: PropTypes.bool,
  theme: PropTypes.string,
  validations: PropTypes.object,
  validationErrors: PropTypes.object,
}

const defaultProps = {
  keywords: [],
  meta: 'mergetags',
  theme: 'vs',
  width: '100%',
  height: '250px',
  enableCreateAutocompletion: false,
}

function AutocompleteEditor(props) {
  const {
    keywords: propsKeywords = [],
    meta,
    mode,
    width,
    height,
    onChange,
    value = '',
    setValue,
    enableCreateAutocompletion,
    theme,
    validations,
    validationErrors,
  } = props

  const [keywords, setKeywords] = useState([])
  const editorRef = useRef(null)
  const monacoRef = useRef(null)
  const completionProviderRef = useRef(null)

  const textKeywords = useMemo(() => {
    if (!value || !enableCreateAutocompletion) {
      return []
    }

    const extractedKeywords = []
    try {
      for (const [, key] of value.matchAll(/{(\w+)}/g)) {
        extractedKeywords.push(key)
      }
    } catch (e) {
      void e
    }
    return extractedKeywords
  }, [value, enableCreateAutocompletion])

  useEffect(() => {
    const combinedKeywords = uniq([...propsKeywords, ...textKeywords])
    setKeywords(combinedKeywords)
  }, [propsKeywords, textKeywords])

  const handleChange = newValue => {
    setValue && setValue(newValue)
    onChange && onChange(newValue)
  }

  const createNewKeyword = useCallback(keyword => {
    setKeywords(prev => {
      if (!prev.includes(keyword)) {
        return [...prev, keyword]
      }
      return prev
    })
  }, [])

  const language = useMemo(() => {
    const modeMap = {
      plain_text: 'plaintext',
      json: 'json',
      xml: 'xml',
      html: 'html',
      javascript: 'javascript',
      typescript: 'typescript',
      css: 'css',
    }
    return modeMap[mode] || mode || 'plaintext'
  }, [mode])

  useEffect(() => {
    if (!monacoRef.current) {
      return
    }

    const monaco = monacoRef.current

    const completionProvider = {
      triggerCharacters: ['{'],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position)
        const line = model.getLineContent(position.lineNumber)
        const charBefore = line[position.column - 2] || ''
        const charAfter = line[position.column - 1] || ''
        const initialBraces = charBefore === '{' ? '' : '{'
        const finalBraces = charAfter === '}' ? '' : '}'

        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.word ? word.startColumn : position.column,
          endColumn: word.word ? word.endColumn : position.column,
        }

        const filteredKeywords = word.word ? keywords.filter(keyword => keyword.toLowerCase().includes(word.word.toLowerCase())) : keywords

        const suggestions = filteredKeywords.map(keyword => ({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Text,
          insertText: `${initialBraces}${keyword}${finalBraces}`,
          range,
          detail: meta,
          filterText: keyword,
          sortText: `0_${keyword}`,
        }))

        if (enableCreateAutocompletion && word.word && !filteredKeywords.includes(word.word)) {
          suggestions.push({
            label: `Create the mergetag "${word.word}"`,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: `${initialBraces}${word.word}${finalBraces}`,
            range,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            command: {
              id: `createKeyword-${word.word}`,
              title: 'Create keyword',
            },
            sortText: `1_${word.word}`,
          })
        }

        return { suggestions, incomplete: false }
      },
      resolveCompletionItem: (item, _token) => {
        if (item.command && item.command.id && item.command.id.startsWith('createKeyword-')) {
          const keyword = item.command.id.replace('createKeyword-', '')
          createNewKeyword(keyword)
        }
        return item
      },
    }

    const disposable = monaco.languages.registerCompletionItemProvider(language, completionProvider)
    completionProviderRef.current = disposable

    return () => {
      disposable.dispose()
    }
  }, [keywords, language, meta, enableCreateAutocompletion, createNewKeyword])

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor
    monacoRef.current = monaco

    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Space, () => {
      editor.trigger('autocomplete', 'editor.action.triggerSuggest', {})
    })
  }

  const getErrorMessage = useMemo(() => {
    if (!validations || !validationErrors) {
      return null
    }

    for (const key of Object.keys(validations)) {
      const validationFn = validations[key]
      const isValid = validationFn(key, value)
      if (!isValid) {
        return validationErrors[key] || null
      }
    }
    return null
  }, [validations, validationErrors, value])

  const isValid = useMemo(() => {
    if (!validations) {
      return true
    }

    for (const key of Object.keys(validations)) {
      const validationFn = validations[key]
      const isValid = validationFn(key, value)
      if (!isValid) {
        return false
      }
    }
    return true
  }, [validations, value])

  const invalid = !isValid && value !== ''
  const editorClassName = classnames({
    'is-invalid': invalid,
    codeeditor: true,
    'border twy-border-base': true,
  })

  return (
    <>
      <div className={editorClassName}>
        <CodeEditor
          language={language}
          theme={theme}
          width={width}
          height={height}
          value={value}
          onChange={handleChange}
          onMount={handleEditorDidMount}
          options={{
            automaticLayout: true,
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            wordWrap: 'on',
            lineNumbers: 'on',
            suggestOnTriggerCharacters: true,
            quickSuggestions: {
              other: true,
              comments: false,
              strings: false,
            },
            suggestSelection: 'first',
            tabCompletion: 'on',
            snippetSuggestions: 'top',
            quickSuggestionsDelay: 100,
          }}
        />
      </div>
      {invalid && getErrorMessage && <span className="invalid-feedback">
        {getErrorMessage}
      </span>
      }
    </>
  )
}

AutocompleteEditor.propTypes = propTypes
AutocompleteEditor.defaultProps = defaultProps

export default AutocompleteEditor
export const AutocompleteEditorFormsy = withFormsy(AutocompleteEditor)
